import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import I18n from '../../../config/i18n';
import Fa from '../../../utils/Fa';
import ApiClient from '../../../utils/ApiClient';
import { accommodationLinkPath } from '../../../components/host/AccommodationLink';
import { RootState } from '../../../config/redux/types';

import './IdSearch.css';

interface IdSearchResult {
  data: {
    location: string;
  };
}

export default function IdSearch(): JSX.Element {
  const { lang } = useParams<'lang'>();
  const { arrival, durationOfStay, occupancy } = useSelector((state: RootState) => state.request);
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const [searchId, setSearchId] = useState('');

  function handleErrorClick(): void {
    setError(false);
  }

  function handleRequestFailure(): void {
    setError(true);
  }

  function handleRequestSuccess({ data }: IdSearchResult): void {
    if (data && data.location) {
      const matched = data.location.match(/\/([\w-_]+)$/);
      if (!matched || matched.length < 2) {
        return;
      }
      const slug = matched[1];
      setSearchId('');
      navigate(accommodationLinkPath(lang, { slug }, arrival, durationOfStay, occupancy));
    } else {
      handleRequestFailure();
    }
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (searchId) {
      setError(false);
      ApiClient.getIdSearch(I18n.locale, searchId)
        .then(handleRequestSuccess)
        .catch(handleRequestFailure);
    }
  }

  function renderErrorMessage(): JSX.Element | null {
    if (error) {
      return (
        <div className="id-search-error-origin">
          <button
            type="button"
            className="id-search-error"
            onClick={handleErrorClick}
            aria-label="Close item"
          >
            {I18n.t('search.micro_search_no_results')}
            <Fa icon="times" className="id-search-error-close" />
          </button>
        </div>
      );
    }
    return null;
  }

  return (
    <form className="id-search" onSubmit={handleSubmit}>
      <input
        value={searchId}
        onChange={(e) => {
          setSearchId(e.currentTarget.value);
        }}
        className="id-search-input"
        placeholder={I18n.t('search.search_by_object_code')}
      />
      <button type="submit" className="id-search-submit" aria-label="Start search">
        <Fa icon="search" className="id-search-icon" />
      </button>
      {renderErrorMessage()}
    </form>
  );
}
